/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { Form } from "@carrotfertility/carotene-core-x"
import { useSelector } from "react-redux"
import { getBenefitConfiguration } from "../../../../reducers/benefitConfiguration"
import { useCurrentUser } from "../../../context/user/UserContext"
import { useCheckRoutingInfoQueryCache, useUpdateRoutingInfo } from "components/carrot-plans/shared/useQueryRoutingInfo"
import { RoutingInfo } from "types/routingInfo"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import {
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowRadioGroup,
  RoutingFlowTitle,
  StepLayout,
  useConvertTranslatedLabel
} from "components/carrot-plans/shared"
import { useFlags } from "launchdarkly-react-client-sdk"

export const mostHelpfulOptions = [
  { label: defineMessage({ defaultMessage: "Discuss my options and journey" }), value: "DISCUSS_OPTIONS" },
  {
    label: defineMessage({ defaultMessage: "Chat about menstrual cycles and evaluating fertility" }),
    value: "CHAT_FERTILITY"
  },
  { label: defineMessage({ defaultMessage: "Chat about nutrition and lifestyle" }), value: "CHAT_LIFESTYLE" },
  { label: defineMessage({ defaultMessage: "Chat about health-related questions" }), value: "CHAT_HEALTH" },
  { label: defineMessage({ defaultMessage: "Help with pathways or regulations" }), value: "HELP_PATHWAY" },
  { label: defineMessage({ defaultMessage: "Help finding or choosing a provider" }), value: "HELP_FINDING_PROVIDER" },
  { label: defineMessage({ defaultMessage: "LGBTQ+ family-forming guidance" }), value: "LGBTQ_FAMILY" },
  { label: defineMessage({ defaultMessage: "Financial information or reimbursement" }), value: "FINANCE_INFO" },
  { label: defineMessage({ defaultMessage: "Advice on making the most of my benefit" }), value: "BENEFIT_ADVICE" },
  { label: defineMessage({ defaultMessage: "I’m not sure" }), value: "NOT_SURE" }
]
export const helpOptionsByJourney = {
  ADOPTION: [
    "DISCUSS_OPTIONS",
    "HELP_PATHWAY",
    "HELP_FINDING_PROVIDER",
    "LGBTQ_FAMILY",
    "FINANCE_INFO",
    "BENEFIT_ADVICE",
    "NOT_SURE"
  ],
  GESTATIONAL: [
    "DISCUSS_OPTIONS",
    "HELP_PATHWAY",
    "HELP_FINDING_PROVIDER",
    "LGBTQ_FAMILY",
    "FINANCE_INFO",
    "BENEFIT_ADVICE",
    "NOT_SURE"
  ],
  PRESERVATION: [
    "DISCUSS_OPTIONS",
    "CHAT_HEALTH",
    "LGBTQ_FAMILY",
    "FINANCE_INFO",
    "BENEFIT_ADVICE",
    "HELP_FINDING_PROVIDER",
    "NOT_SURE"
  ],
  ASSISTED_REPRODUCTION: [
    "DISCUSS_OPTIONS",
    "CHAT_HEALTH",
    "LGBTQ_FAMILY",
    "FINANCE_INFO",
    "BENEFIT_ADVICE",
    "HELP_FINDING_PROVIDER",
    "NOT_SURE"
  ],
  TRY_PREGNANT: [
    "DISCUSS_OPTIONS",
    "CHAT_FERTILITY",
    "CHAT_LIFESTYLE",
    "LGBTQ_FAMILY",
    "FINANCE_INFO",
    "BENEFIT_ADVICE",
    "HELP_FINDING_PROVIDER",
    "NOT_SURE"
  ],
  PREGNANT: ["CHAT_LIFESTYLE", "CHAT_HEALTH", "FINANCE_INFO", "LGBTQ_FAMILY", "BENEFIT_ADVICE", "NOT_SURE"],
  EXPLORING: ["CHAT_HEALTH", "LGBTQ_FAMILY", "FINANCE_INFO", "BENEFIT_ADVICE", "HELP_FINDING_PROVIDER", "NOT_SURE"],
  MENOPAUSE: [
    "DISCUSS_OPTIONS",
    "HELP_FINDING_PROVIDER",
    "CHAT_FERTILITY",
    "CHAT_LIFESTYLE",
    "CHAT_HEALTH",
    "BENEFIT_ADVICE",
    "NOT_SURE"
  ],
  LOW_TESTOSTERONE: [
    "DISCUSS_OPTIONS",
    "HELP_FINDING_PROVIDER",
    "CHAT_LIFESTYLE",
    "CHAT_HEALTH",
    "BENEFIT_ADVICE",
    "NOT_SURE"
  ],
  GENDER_AFFIRMING_CARE: [
    "DISCUSS_OPTIONS",
    "HELP_FINDING_PROVIDER",
    "CHAT_HEALTH",
    "LGBTQ_FAMILY",
    "FINANCE_INFO",
    "BENEFIT_ADVICE",
    "NOT_SURE"
  ]
}

function filterOptions(
  helpOptions,
  hideLGBTQGuidanceOption,
  carrotLite,
  hideChatAboutCyclesOptionForNoCycles,
  hideFinanceOptionForPregnantJourney
): string[] {
  let filteredOptions = helpOptions
  if (hideLGBTQGuidanceOption) {
    filteredOptions = filteredOptions.filter((opt) => opt !== "LGBTQ_FAMILY")
  }
  if (carrotLite || hideFinanceOptionForPregnantJourney) {
    filteredOptions = filteredOptions.filter((opt) => opt !== "FINANCE_INFO")
  }
  if (hideChatAboutCyclesOptionForNoCycles) {
    filteredOptions = filteredOptions.filter((opt) => opt !== "CHAT_FERTILITY")
  }
  return filteredOptions
}

function useOptionsForJourneyType(
  routingData: RoutingInfo
): { buttonOptions: { label: { defaultMessage: string }; value: string }[] } {
  const { enablePostpartumRoutingSupport } = useFlags()
  const memberJourney = routingData?.journey
  const lgbtqEnabled = useSelector(getBenefitConfiguration).featureConfig.isLgtbqEnabled
  const memberJourneyStage = routingData?.journeyStage
  const hideChatAboutCyclesOptionForNoCycles: boolean = memberJourneyStage === "NO_MENSTRUAL_CYCLES"
  const hideLGBTQGuidanceOption: boolean =
    !lgbtqEnabled || memberJourneyStage === "POSTPARTUM_PARENTING" || memberJourneyStage === "PARENTING"
  const { company } = useCurrentUser()
  // WQ-1470, when removing the feature flag remove this const and its use.
  const hideFinanceOptionForPregnantJourney: boolean = memberJourney === "PREGNANT" && !enablePostpartumRoutingSupport

  const journeyHelpOptions = filterOptions(
    helpOptionsByJourney[memberJourney] || [],
    hideLGBTQGuidanceOption,
    company?.carrotLite,
    hideChatAboutCyclesOptionForNoCycles,
    hideFinanceOptionForPregnantJourney
  )
  const buttonOptions = mostHelpfulOptions.filter((option) => journeyHelpOptions.includes(option.value))
  return {
    buttonOptions
  }
}

export default function MostHelpfulStep(): JSX.Element {
  const { mutateAsync } = useUpdateRoutingInfo()
  const routingData = useCheckRoutingInfoQueryCache()
  const defaultValue = routingData?.mostHelpfulNow
  const { buttonOptions } = useOptionsForJourneyType(routingData)
  const radioOptions = useConvertTranslatedLabel(buttonOptions)
  const { send } = useStateMachine(defaultValue ? "" : null)
  const intl = useIntl()

  async function onClickContinue(formValues: { mostHelpfulNow: string }): Promise<void> {
    await mutateAsync(formValues, { onSuccess: () => send("") })
  }

  const header = intl.formatMessage({
    defaultMessage: "What would be most helpful right now?"
  })

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={<span id="mostHelpfulNow-label">{header}</span>}
        description={intl.formatMessage({
          defaultMessage: "Choose the option that feels like the top priority, just to get things started."
        })}
        formElements={
          <Form defaultValues={{ mostHelpfulNow: defaultValue }} onSubmit={onClickContinue}>
            <RoutingFlowRadioGroup
              name="mostHelpfulNow"
              controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
              aria-labelledby="mostHelpfulNow-label"
              options={radioOptions}
            />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Form>
        }
      />
    </>
  )
}
